
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useGetEnglishQuestionGroupsBySubjectQuery,
  useLazyGetEnglishQuestionGroupsBySubjectQuery,
} from "../../slices/englishQuestionGroupApiSlice";

import {
  useGetQuestionBankSubjectsQuery,
} from "../../slices/questionBankSubjectApiSlice";

const EnglishQuestionGroupList = () => {
  const navigate = useNavigate();

  // ======================================================
  // Selected Subject
  // ======================================================

  const [selectedSubject, setSelectedSubject] =
    useState("");

  // ======================================================
  // Question Bank Subjects
  // ======================================================

  const {
    data: subjectsData,
    isLoading: isSubjectsLoading,
    isError: isSubjectsError,
    error: subjectsError,
  } = useGetQuestionBankSubjectsQuery();

  // ======================================================
  // Normalize Subjects
  // ======================================================

  const subjects = useMemo(() => {
    if (Array.isArray(subjectsData)) {
      return subjectsData;
    }

    if (Array.isArray(subjectsData?.subjects)) {
      return subjectsData.subjects;
    }

    if (Array.isArray(subjectsData?.data)) {
      return subjectsData.data;
    }

    return [];
  }, [subjectsData]);

  // ======================================================
  // Find Subjects With English Question Groups
  // ======================================================

  const [
    checkSubjectGroups,
    {
      isLoading: isCheckingSubjects,
    },
  ] = useLazyGetEnglishQuestionGroupsBySubjectQuery();

  const [subjectsWithGroups, setSubjectsWithGroups] =
    useState([]);

  useEffect(() => {
    if (!subjects.length) {
      setSubjectsWithGroups([]);
      return;
    }

    let cancelled = false;

    const findSubjectsWithGroups = async () => {
      const validSubjects = [];

      for (const subject of subjects) {
        const subjectId =
          subject._id || subject.id;

        if (!subjectId) {
          continue;
        }

        try {
          const response =
            await checkSubjectGroups(
              subjectId
            ).unwrap();

          const groups =
            response?.groups ||
            response?.questionGroups ||
            (Array.isArray(response)
              ? response
              : response?.data || []);

          if (
            Array.isArray(groups) &&
            groups.length > 0
          ) {
            validSubjects.push(subject);
          }
        } catch (error) {
          // Ignore subjects without accessible groups.
        }
      }

      if (!cancelled) {
        setSubjectsWithGroups(validSubjects);
      }
    };

    findSubjectsWithGroups();

    return () => {
      cancelled = true;
    };
  }, [subjects, checkSubjectGroups]);

  // ======================================================
  // Load Groups For Selected Subject
  // ======================================================

  const {
    data: groupsData,
    isLoading: isGroupsLoading,
    isFetching: isGroupsFetching,
    isError: isGroupsError,
    error: groupsError,
  } = useGetEnglishQuestionGroupsBySubjectQuery(
    selectedSubject,
    {
      skip: !selectedSubject,
    }
  );

  // ======================================================
  // Normalize Groups
  // ======================================================

  const groups = useMemo(() => {
    if (Array.isArray(groupsData)) {
      return groupsData;
    }

    if (Array.isArray(groupsData?.groups)) {
      return groupsData.groups;
    }

    if (
      Array.isArray(groupsData?.questionGroups)
    ) {
      return groupsData.questionGroups;
    }

    if (Array.isArray(groupsData?.data)) {
      return groupsData.data;
    }

    return [];
  }, [groupsData]);

  // ======================================================
  // Error Messages
  // ======================================================

  const subjectsErrorMessage =
    typeof subjectsError === "string"
      ? subjectsError
      : subjectsError?.data?.message ||
        subjectsError?.message ||
        "Unable to load subjects.";

  const groupsErrorMessage =
    typeof groupsError === "string"
      ? groupsError
      : groupsError?.data?.message ||
        groupsError?.message ||
        "Unable to load English question groups.";

  // ======================================================
  // Create Question Group
  // ======================================================

  const handleCreate = () => {
    navigate("/english-question-groups/create");
  };

  // ======================================================
  // Render
  // ======================================================

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">

        {/* ==================================================
            Header
        ================================================== */}

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              English Question Groups
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              Select a subject to view its question groups.
            </p>
          </div>

          <button
            type="button"
            onClick={handleCreate}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 5v14M5 12h14"
              />
            </svg>

            Create Question Group
          </button>
        </div>

        {/* ==================================================
            Subject
        ================================================== */}

        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <label
            htmlFor="subject"
            className="mb-2 block text-sm font-medium text-slate-700"
          >
            Subject
          </label>

          <select
            id="subject"
            value={selectedSubject}
            onChange={(event) =>
              setSelectedSubject(event.target.value)
            }
            disabled={
              isSubjectsLoading ||
              isCheckingSubjects ||
              isSubjectsError
            }
            className="w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100"
          >
            <option value="">
              {isSubjectsLoading
                ? "Loading subjects..."
                : isCheckingSubjects
                  ? "Finding subjects..."
                  : "Select subject"}
            </option>

            {subjectsWithGroups.map((subject) => {
              const subjectId =
                subject._id || subject.id;

              const subjectName =
                subject.name ||
                subject.subjectName ||
                subject.title;

              return (
                <option
                  key={subjectId}
                  value={subjectId}
                >
                  {subjectName}
                </option>
              );
            })}
          </select>

          {isSubjectsError && (
            <p className="mt-2 text-sm text-red-600">
              {subjectsErrorMessage}
            </p>
          )}
        </div>

        {/* ==================================================
            No Subject
        ================================================== */}

        {!selectedSubject && (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
            <p className="text-sm text-slate-500">
              Select a subject to view English question groups.
            </p>
          </div>
        )}

        {/* ==================================================
            Loading
        ================================================== */}

        {selectedSubject && isGroupsLoading && (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
            <p className="text-sm text-slate-500">
              Loading question groups...
            </p>
          </div>
        )}

        {/* ==================================================
            Error
        ================================================== */}

        {selectedSubject && isGroupsError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-sm font-medium text-red-800">
              Unable to load question groups
            </p>

            <p className="mt-1 text-sm text-red-600">
              {groupsErrorMessage}
            </p>
          </div>
        )}

        {/* ==================================================
            Empty
        ================================================== */}

        {selectedSubject &&
          !isGroupsLoading &&
          !isGroupsError &&
          groups.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
              <p className="text-sm text-slate-500">
                No English question groups found.
              </p>
            </div>
          )}

        {/* ==================================================
            Groups
        ================================================== */}

        {selectedSubject &&
          !isGroupsLoading &&
          !isGroupsError &&
          groups.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">

              <div className="border-b border-slate-200 px-5 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-slate-900">
                    Question Groups
                  </h2>

                  <span className="text-sm text-slate-500">
                    {groups.length}
                  </span>
                </div>
              </div>

              {isGroupsFetching && (
                <div className="border-b border-slate-100 px-5 py-2 text-xs text-slate-400">
                  Updating...
                </div>
              )}

              <div className="divide-y divide-slate-100">
                {groups.map((group) => (
                  <div
                    key={
                      group._id ||
                      group.id
                    }
                    className="px-5 py-4 hover:bg-slate-50"
                  >
                    <h3 className="text-sm font-semibold text-slate-900">
                      {group.name ||
                        group.title ||
                        group.groupName ||
                        "Untitled Group"}
                    </h3>

                    {group.description && (
                      <p className="mt-1 text-sm text-slate-500">
                        {group.description}
                      </p>
                    )}

                    <div className="mt-2 flex gap-4 text-xs text-slate-400">
                      {group.groupType && (
                        <span>
                          {group.groupType}
                        </span>
                      )}

                      {(group.questionCount !==
                        undefined ||
                        group.questions) && (
                        <span>
                          {group.questionCount ??
                            group.questions?.length ??
                            0}{" "}
                          questions
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </main>
    </div>
  );
};

export default EnglishQuestionGroupList;

